import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Tooltip,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Drawer,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  ClickAwayListener,
  Typography,
} from '@mui/material';
import { PageHeader } from '@/components/ui';
import type { PageHeaderAction } from '@/components/ui/PageHeader';
import {
  FilterList as FilterIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
  Replay as RetryIcon,
  Webhook as WebhookIcon,
  Search as SearchIcon,
  ContentCopy as ContentCopyIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SuperadminWebhookSummaryCards } from '../../components';
import {
  SUPERADMIN_WEBHOOK_STATUSES,
  getSuperadminStatusColor,
  getSuperadminStatusDisplayName,
} from '../../constants';
import type { SuperadminWebhook } from '../../types';
import { useCommonBreadcrumbs } from '@/utils/breadcrumbs';
import { getBreadcrumbConfig } from '@/config/breadcrumbs';
import { format as formatMomentDate } from '@/utils/time';
import { superadminWebhooksService } from '../../../../services/webhooksService';
import { toast } from 'react-hot-toast';
import { FilterPanel } from '../../components/WebhookFilters/FilterPanel';
import { useSuperadminAuth } from '@/superadmin/contexts/SuperadminAuthContext';

export const SuperadminWebhooksPage: React.FC = () => {
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [merchantTransactionSearchTerm, setMerchantTransactionSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedWebhook, setSelectedWebhook] = useState<SuperadminWebhook | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<WebhookFilters>({
    searchTerm: '',
    statusFilter: '',
    webhookStatus: '',
    merchants: [], // ✅ ADD
    dateRange: { startDate: '', endDate: '' },
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [webhooks, setWebhooks] = useState<SuperadminWebhook[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchKey, setSearchKey] = useState<string>('transaction_id');
  const [searchValue, setSearchValue] = useState<string>('');
  const [merchants, setMerchants] = useState<any[]>([]);

  const { user } = useSuperadminAuth();

  if (!user) return <div>No Access</div>;

  const hasPermission = (permission: string) => {
    if (user.is_superuser) return true; // superuser has everything
    return user.permission_list?.includes(permission);
  };

  // Applied filters for API calls
  const [appliedFilters, setAppliedFilters] = useState<{
    transaction_id?: string;
    merchant_transaction_id?: string;
    webhook_status?: string; // ✅ ADD
    transaction_status?: string;
    start_date?: string;
    end_date?: string;
    merchant_uuid?: string;
  }>({});

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const res = await superadminWebhooksService.listMerchants(); // or common service
      setMerchants(res.results || []);
    } catch {
      setMerchants([]);
    }
  };

  const handleSearch = () => {
    const newFilters = {
      [searchKey]: searchValue.trim() || undefined,
    };

    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  // Load webhooks with API integration
  useEffect(() => {
    fetchWebhooks();
  }, [currentPage, pageSize, appliedFilters]);

  const fetchWebhooks = async () => {
    setIsLoading(true);
    try {
      const response = await superadminWebhooksService.list(currentPage, pageSize, appliedFilters);

      setWebhooks(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error loading webhooks:', error);
      toast.error('Failed to load webhooks');
      setWebhooks([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Utility: format webhook dates
  const formatWebhookDate = (dateString: string): string => {
    return formatMomentDate(dateString, 'MMM DD, YYYY HH:mm');
  };

  const handleFilterPanelOpen = () => {
    setIsFilterPanelOpen(true);
  };

  const handleFilterPanelClose = () => {
    setIsFilterPanelOpen(false);
  };

  const handleRefresh = () => {
    fetchWebhooks();
  };

  const handleViewWebhook = (webhook: SuperadminWebhook) => {
    setSelectedWebhook(webhook);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedWebhook(null);
  };

  const handleFiltersChange = (newFilters: WebhookFilters) => {
    console.log('TransactionsPage - Received filters:', newFilters);
    setFilters(newFilters);
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);

    try {
      let startDate = '';
      let endDate = '';
      let status = '';
      let merchantUuids = '';
      let webhookStatus = '';

      // Date range
      if (filters.dateRange?.startDate && filters.dateRange?.endDate) {
        const formatDate = (dateStr: string) => new Date(dateStr).toISOString().split('T')[0];

        startDate = formatDate(filters.dateRange.startDate);
        endDate = formatDate(filters.dateRange.endDate);
      }

      // Status
      if (filters.statusFilter) {
        const statuses = filters.statusFilter.split(',').filter(Boolean);
        if (statuses.length) {
          status = statuses.join(',');
        }
      }

      // ✅ Webhook status
      if (filters.webhookStatus) {
        webhookStatus = filters.webhookStatus;
      }

      // Merchants
      if (filters.merchants && filters.merchants.length > 0) {
        merchantUuids = filters.merchants.map(m => m.uuid).join(',');
      }

      setAppliedFilters(prev => ({
        ...prev,
        ...(status && { transaction_status: status }),
        ...(webhookStatus && { webhook_status: webhookStatus }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
        ...(merchantUuids && { merchant_uuid: merchantUuids }),
      }));

      setCurrentPage(1);
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.error('Failed to apply filters');
    } finally {
      setIsLoading(false);
      setIsFilterPanelOpen(false);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: '',
      merchants: [],
      dateRange: { startDate: '', endDate: '' },
    });
    setAppliedFilters({});
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Handle retry webhook
  const handleRetryWebhook = async (webhook: SuperadminWebhook) => {
    try {
      await superadminWebhooksService.retry(webhook.id.toString());
      toast.success('Webhook retry initiated');
      // Refresh the webhooks list
      fetchWebhooks();
    } catch (error) {
      console.error('Error retrying webhook:', error);
      toast.error('Failed to retry webhook');
    }
  };

  // Get subtitle with count
  const getSubtitle = () => {
    return `${totalCount} ${totalCount === 1 ? 'webhook' : 'webhooks'}`;
  };

  const handleExport = () => {
    console.log('Export clicked');
  };

  // Breadcrumbs configuration
  const breadcrumbConfig = getBreadcrumbConfig('superadmin');
  const finalBreadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: breadcrumbConfig.hideBreadcrumbs,
  });

  // Page header actions
  const headerActions: PageHeaderAction[] = [
    // {
    //   label: '',
    //   icon: <RefreshIcon />,
    //   onClick: handleSearch,
    //   variant: 'text',
    //   size: 'small',
    // },
  ];

  // Use webhooks directly from API (server-side pagination)
  const displayWebhooks = webhooks;

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Webhooks"
        subtitle={getSubtitle()}
        breadcrumbs={finalBreadcrumbs}
        actions={headerActions}
        showDivider={false}
      />

      {/* Webhook Summary Cards */}
      <SuperadminWebhookSummaryCards webhooks={displayWebhooks} />

      {/* Webhook Filters */}
      <Box
        mb={3}
        display="flex"
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        justifyContent="space-between"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      >
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200, background: 'white' }}>
            <Select value={searchKey} onChange={e => setSearchKey(e.target.value)}>
              <MenuItem value="transaction_id">Transaction ID</MenuItem>
              <MenuItem value="merchant_transaction_id">Merchant Transaction ID</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            type="search"
            placeholder={`Enter ${searchKey.replace(/_/g, ' ')}`}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 300,
              '& .MuiOutlinedInput-root': {
                background: 'white',
              },
            }}
          />

          {/* Clear Filters Button */}
          <Button variant="text" size="small" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Box>
        <Box>
          <Button variant="outlined" size="small" onClick={handleExport} startIcon={<ExportIcon />}>
            Export
          </Button>
          <Button onClick={handleRefresh} size="small" variant="text">
            <RefreshIcon />
          </Button>
        </Box>
      </Box>

      <Box gap={1} mb={2}>
        {/* Filter Button */}
        <Button
          ref={filterButtonRef}
          variant="outlined"
          size="small"
          onClick={handleFilterPanelOpen}
          endIcon={<FilterIcon />}
          startIcon={<AddIcon />}
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            textTransform: 'none',
          }}
        >
          Add Filters
        </Button>
        {isFilterPanelOpen && (
          <ClickAwayListener onClickAway={handleFilterPanelClose}>
            <Box>
              <FilterPanel
                open={isFilterPanelOpen}
                anchorEl={filterButtonRef.current}
                onClose={handleFilterPanelClose}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                merchants={merchants} // ✅ ADD
              />
            </Box>
          </ClickAwayListener>
        )}
      </Box>

      {/* Webhooks Table */}
      <Paper elevation={1}>
        {hasPermission('ADMIN_WEBHOOK_LIST_VIEW') ? (
          <Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Webhook URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Retry Count</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : displayWebhooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          No webhooks found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayWebhooks.map(webhook => (
                      <TableRow key={webhook.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* <WebhookIcon color="action" sx={{ fontSize: 16 }} /> */}
                            <Typography variant="body2" fontFamily="monospace">
                              {webhook.transaction_id}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigator.clipboard.writeText(String(webhook.transaction_id))
                                }
                                className="copy-icon"
                                sx={{ ml: 0.5, p: 0.25, opacity: 100, transition: 'opacity 0.2s' }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {webhook.merchant_transaction_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {webhook.webhook_url}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getSuperadminStatusDisplayName(webhook.webhook_status)}
                            color={getSuperadminStatusColor(webhook.webhook_status) as any}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{webhook.tries}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatWebhookDate(webhook.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewWebhook(webhook)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(webhook.webhook_status === 'failure' ||
                              webhook.webhook_status === 'timeout') && (
                              <Tooltip title="Retry Webhook">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRetryWebhook(webhook)}
                                >
                                  <RetryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={pageSize}
              page={currentPage - 1} // Convert 1-based to 0-based for Material-UI
              onPageChange={(_, newPage) => handlePageChange(newPage + 1)} // Convert 0-based to 1-based
              onRowsPerPageChange={handlePageSizeChange}
            />
          </Box>
        ) : (
          <Typography sx={{ p: 4 }} variant="body1" color="textSecondary">
            You do not have permission to view webhooks.
          </Typography>
        )}
      </Paper>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            padding: 3,
            pt: 8,
            overflowY: 'auto',
          },
        }}
      >
        {selectedWebhook ? (
          <Box display="flex" flexDirection="column" gap={3} sx={{ position: 'relative' }}>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                position: 'absolute',
                top: -24,
                right: 8,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Transaction ID */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700}>Transaction ID</Typography>
              <Typography fontFamily="monospace" mt={0.5}>
                {selectedWebhook.transaction_id || 'N/A'}
              </Typography>
            </Paper>

            {/* Merchant Transaction ID */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700}>Merchant Transaction ID</Typography>
              <Typography fontFamily="monospace" mt={0.5}>
                {selectedWebhook.merchant_transaction_id || 'N/A'}
              </Typography>
            </Paper>

            {/* Webhook URL */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700}>Webhook URL</Typography>
              <Typography sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                {selectedWebhook.webhook_url}
              </Typography>
            </Paper>

            {/* Status & Retry */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box display="flex" flexDirection="column" gap={1.2}>
                <Box display="flex">
                  <Box width="40%" fontWeight={600}>
                    Status
                  </Box>
                  <Box width="60%">
                    <Chip
                      label={getSuperadminStatusDisplayName(selectedWebhook.webhook_status)}
                      color={getSuperadminStatusColor(selectedWebhook.webhook_status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Box display="flex">
                  <Box width="40%" fontWeight={600}>
                    Retry Count
                  </Box>
                  <Box width="60%">{selectedWebhook.tries}</Box>
                </Box>
              </Box>
            </Paper>

            {/* Created At */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={700}>Created At</Typography>
              <Typography mt={0.5}>{formatWebhookDate(selectedWebhook.created_at)}</Typography>
            </Paper>
          </Box>
        ) : (
          <Box>
            <Typography>Select a webhook to view details.</Typography>
            <Button variant="outlined" onClick={handleCloseDrawer} fullWidth>
              Close
            </Button>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SuperadminWebhooksPage;
